/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { PdfSection } from './pdf-section';
import { Logbook } from '../../model/logbook/logbook';
import { DatePipe } from '@angular/common';
import { PdfHeader } from '../pdf-header';
import { PdfUtils } from '../pdf-utils';
import { TranslateService } from '@ngx-translate/core';

export class LogbookPdfSection extends PdfSection {
  private logbook: Logbook;
  constructor(title: string, logbook: Logbook) {
    super(title);
    this.logbook = logbook;
  }
  build(
    trl: TranslateService,
    doc,
    header: PdfHeader,
    x: number,
    y: number
  ): number {
    const elements = this.logbook.build();
    doc.setDrawColor(47, 55, 58);
    doc.setTextColor(47, 55, 58);
    doc.setFontSize(10);

    const x2 = doc.internal.pageSize.getWidth() - 30 - x;
    doc.rect(x, y, x2, 30); // Rect logo
    doc.setFont('roboto', 'bold');
    doc.text(
      x + this.margin.w + 1,
      y + this.margin.h,
      this.title.toUpperCase()
    );
    let newY = y + 30;
    const maxHeight = doc.internal.pageSize.getHeight() - 30;
    if (elements.length > 0) {
      const textHeight = doc.getTextDimensions('To').h;
      for (const elem of elements) {
        // Write date
        doc.setFontSize(10);
        doc.setFont('roboto', 'bold');
        if (newY + textHeight + this.margin.h * 2 > maxHeight) {
          newY += textHeight;
          doc.rect(x, y, x2, newY - y + 10); // Rect logo
          PdfUtils.addPage(doc, header);
          y = header.height;
          newY = header.height + this.margin.h;
        }
        const date = new DatePipe('fr').transform(
          elem.date,
          'le dd/MM/yyyy à HH:mm'
        );
        doc.text(
          x + this.margin.w,
          newY + this.margin.h,
          `#${elem.indice} - ${date}`
        );
        newY += textHeight;
        doc.setFont('roboto', 'normal');

        // Write author
        if (elem.author) {
          if (newY + textHeight + this.margin.h * 2 > maxHeight) {
            newY += textHeight;
            doc.rect(x, y, x2, newY - y + 10); // Rect logo
            PdfUtils.addPage(doc, header);
            y = header.height;
            newY = header.height + this.margin.h;
          }
          doc.setFontSize(8);
          doc.text(
            x + this.margin.w,
            newY + this.margin.h,
            'par ' + elem.author
          );
          newY += textHeight;
          doc.setFontSize(10);
        }

        // Write content
        if (elem.content) {
          const height =
            doc.getTextDimensions(elem.content).h +
            (elem.content.split('\n').length - 1) * 5;
          if (height < maxHeight - header.height - this.margin.h * 2) {
            if (newY + height + this.margin.h * 2 > maxHeight) {
              doc.rect(x, y, x2, newY - y + 10); // Rect logo
              PdfUtils.addPage(doc, header);
              y = header.height;
              newY = header.height + this.margin.h;
            }
            doc.text(x + this.margin.w, newY + this.margin.h, elem.content);
            newY += height;
          }
        }

        // Write status
        if (elem.change && elem.change.length > 0) {
          newY = this.generateChangeStatus(
            elem.change,
            doc,
            x + this.margin.w,
            newY
          );
        }
        newY += textHeight + 5;
        if (newY + textHeight + this.margin.h * 2 > maxHeight) {
          doc.rect(x, y, x2, newY - y - 5); // Rect logo
          PdfUtils.addPage(doc, header);
          y = header.height;
          newY = header.height + this.margin.h;
        }
      }
    }
    doc.rect(x, y, x2, newY - y + 10); // Rect logo
    return newY;
  }

  getDimension(doc: unknown): { w: number; h: number } {
    void doc;
    return { h: 0, w: 0 };
  }

  private generateChangeStatus(
    change: { field: string; before: string; after: string }[],
    doc,
    x: number,
    y: number
  ): number {
    let newY = y;
    const rowHeight = doc.getTextDimensions('C').h;
    for (const ch of change) {
      let newX = x;
      doc.setFont('roboto', 'bold');
      doc.text(newX, newY + this.margin.h, ch.field + ': ');
      doc.setFont('roboto', 'normal');
      newX += this.getTextWidth(doc, ch.field + ': ');
      doc.text(newX, newY + this.margin.h, 'Modifié de');
      newX += this.getTextWidth(doc, 'Modifié de');
      doc.setTextColor(72, 207, 173);
      doc.setFont('roboto', 'bold');
      doc.text(newX, newY + this.margin.h, ch.before);
      newX += this.getTextWidth(doc, ch.before);
      doc.setTextColor(47, 55, 58);
      doc.setFont('roboto', 'normal');
      doc.text(newX, newY + this.margin.h, 'à');
      newX += this.getTextWidth(doc, 'à');
      doc.setTextColor(72, 207, 173);
      doc.setFont('roboto', 'bold');
      doc.text(newX, newY + this.margin.h, ch.after);
      doc.setTextColor(47, 55, 58);
      doc.setFont('roboto', 'normal');
      newY = newY + rowHeight;
    }

    return newY;
  }
  public getTextWidth(doc, text: string) {
    return doc.getTextDimensions(text).w + 1 + text.length * 0.35;
  }
}
